import type { Role } from '@revcall/shared';
import { apiClient } from '../client';

/**
 * W6: read-only facts behind the super admin's platform pages
 * (server/src/adapters/inbound/http/admin/platform.routes.ts), and the paged
 * numbers of every organisation (SR1-1.2).
 */

export type TwilioEnvVar = 'TWILIO_ACCOUNT_SID' | 'TWILIO_AUTH_TOKEN' | 'TWILIO_API_KEY' | 'TWILIO_API_SECRET' | 'TWILIO_TWIML_APP_SID';

export interface TwilioHealth {
  credentials: Record<TwilioEnvVar, boolean>;
  account:
    | { checked: false }
    | { checked: true; reachable: true; status: string; friendly_name: string; type: string }
    | { checked: true; reachable: false; http_status: number | null; error: string };
  balance: { amount: string; currency: string } | null;
  twiml_app:
    | { checked: false }
    | { checked: true; found: true; sid: string; friendly_name: string; voice_url: string; voice_method: string; status_callback: string }
    | { checked: true; found: false; http_status: number | null; error: string };
  expected_voice_url: string;
}

export interface CallingProviderOverview {
  provider: 'twilio' | 'asterisk';
  fallback_reason: string;
  pilot_emails: string[];
  members: Array<{ email: string; full_name: string; role: Role; organisation_id: string; organisation_name: string }>;
  organisations: number;
  usage: { period_days: number; from: string; calls: number; started_minute: number; per_second: number };
}

export type AmdDetector = 'transcript_jev' | 'transcript_rules' | 'swoft_model' | 'twilio_legacy' | 'other';

export interface AmdHealth {
  requested_engine: 'transcript_jev' | 'swoft_model';
  engine: 'transcript_jev' | 'swoft_model';
  fallback_reason: string;
  shadow: boolean;
  setting_updated_at: string | null;
  period_days: number;
  decisions: { total: number; by_detector: Array<{ detector: AmdDetector; human: number; machine: number; unknown: number }> };
}

export interface PlatformNumber {
  id: string;
  phone_number: string;
  twilio_sid: string | null;
  tenant_email: string;
  area_code: string;
  status: string;
  capabilities: string[];
  provisioned_at: string | null;
}

export function getTwilioHealth(signal?: AbortSignal) {
  return apiClient.get<TwilioHealth>('/admin/platform/twilio-health', { signal });
}

export function getCallingProviderOverview(signal?: AbortSignal) {
  return apiClient.get<CallingProviderOverview>('/admin/platform/calling-provider-overview', { signal });
}

export function getAmdHealth(signal?: AbortSignal) {
  return apiClient.get<AmdHealth>('/admin/platform/amd-health', { signal });
}

export function listPlatformNumbers(params: { q?: string; limit: number; offset: number }, signal?: AbortSignal) {
  const qs = new URLSearchParams({ limit: String(params.limit), offset: String(params.offset) });
  if (params.q?.trim()) qs.set('q', params.q.trim());
  return apiClient.get<{ numbers: PlatformNumber[]; total: number; limit: number; offset: number }>(`/telephony/admin/all-numbers?${qs.toString()}`, { signal });
}
